// acneScarsRemovalData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const bloodtestDubai: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Blood Test at Home in Dubai | Elite Body Home Polyclinic",
            "description": "Premium home blood testing services in Dubai with DHA-certified nurses. Over 2000 tests available including allergy tests, diabetes screening, STD tests and full body checkups. Book your appointment today.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/blood-test-at-home-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Blood Test at Home in Dubai | Elite Body Home Polyclinic",
                "description": "Premium home blood testing services in Dubai with DHA-certified nurses. Over 2000 tests available including allergy tests, diabetes screening, STD tests and full body checkups. Book your appointment today.",
                "url": "https://elitebodyhome.com/services/blood-test-at-home-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Blood-Test.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Blood-Test.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Home Blood Test Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Blood Test at Home in Dubai | Elite Body Home Polyclinic",
                "description": "Premium home blood testing services in Dubai with DHA-certified nurses. Over 2000 tests available including allergy tests, diabetes screening, STD tests and full body checkups. Book your appointment today.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Blood-Test.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Blood Test at Home Dubai"],
        "hero": {
            "title": "Blood Test at Home Services",
            "subtitle": "",
            "description": "Why go out for a blood test when it could come to you? Elite Body Home makes health checks easier, safer and stress-free through our blood test at home service. Avoid waiting rooms and get accurate, reliable results in the comfort of your office or home. Whether it's regular wellness checks or specialized tests, our expert team makes it easy to stay updated about your health."
        },
        "howItWorks": {
            "title": "Home Blood Test Process",
            "description": "Our home blood testing service in Dubai is designed for maximum convenience while maintaining the highest standards of accuracy and hygiene."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Book Appointment",
                    "desc": "Contact us or book online and our care experts will assist you in scheduling.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Nurse Visit",
                    "desc": "A DHA-certified nurse arrives at your chosen location with all necessary equipment.",
                    "icon": "fa-solid fa-user-nurse"
                },
                {
                    "title": "Sample Collection",
                    "desc": "Safe and quick blood sample collection completed in 10-15 minutes.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Lab Processing",
                    "desc": "Samples sent to our CAP-accredited partner laboratories for analysis.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "Receive Results",
                    "desc": "Get your results electronically the same day via email or secure portal.",
                    "icon": "fa-solid fa-envelope"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Home Blood Testing",
            "subheading": "Why home blood testing makes sense",
            "para": "Choosing home blood testing offers numerous advantages that make this service ideal for many patients:",
            "items": [
                {
                    "title": "Convenience",
                    "desc": "No need to travel or wait in long queues - get tested at your preferred time and location.",
                    "icon": "fa-solid fa-thumbs-up"
                },
                {
                    "title": "Comfort",
                    "desc": "Ideal for seniors, children, or people with mobility challenges who find travel difficult.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "Time Saving",
                    "desc": "Avoid waiting for hours at labs or hospitals - the entire process is quick and efficient.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Privacy",
                    "desc": "Enjoy a private testing experience away from public spaces.",
                    "icon": "fa-solid fa-user-secret"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit",
            "subheading": "",
            "para": "Our home blood testing service is suitable for:",
            "items": [
                {
                    "title": "Busy Professionals",
                    "desc": "Those with tight schedules who can't visit labs during working hours.",
                    "icon": "fa-solid fa-briefcase"
                },
                {
                    "title": "Elderly Patients",
                    "desc": "Seniors who find traveling to medical facilities challenging.",
                    "icon": "fa-solid fa-wheelchair"
                },
                {
                    "title": "Parents with Kids",
                    "desc": "Families who want to avoid the stress of taking children to labs.",
                    "icon": "fa-solid fa-child"
                },
                {
                    "title": "Immunocompromised",
                    "desc": "Those needing to limit exposure to public spaces for health reasons.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "preparationTips": {
            "heading": "How to Prepare",
            "subheading": "",
            "para": "To ensure accurate test results, please follow these preparation guidelines:",
            "items": [
                {
                    "title": "Fasting Requirements",
                    "desc": "Some tests require fasting - confirm with our team when booking.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Drink plenty of water before your test for easier blood draw.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Medication Info",
                    "desc": "Have details of any medications you're currently taking ready.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Comfortable Clothing",
                    "desc": "Wear loose clothing that allows easy access to your arm.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "Understanding Your Results",
            "subheading": "",
            "para": "Our comprehensive reporting makes it easy to understand your health status:",
            "items": [
                {
                    "title": "Quick Turnaround",
                    "desc": "Most results available the same day via secure digital delivery.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Expert Review",
                    "desc": "All results reviewed by DHA-licensed pathologists for accuracy.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "Clear Formatting",
                    "desc": "Easy-to-read reports with reference ranges highlighted.",
                    "icon": "fa-solid fa-file-lines"
                },
                {
                    "title": "Follow-up Support",
                    "desc": "Guidance available if you have questions about your results.",
                    "icon": "fa-solid fa-headset"
                }
            ]
        },
        "aftercareTips": {
            "heading": "After Your Test",
            "subheading": "",
            "para": "After your blood draw, we recommend these simple steps:",
            "items": [
                {
                    "title": "Apply Pressure",
                    "desc": "Keep pressure on the puncture site to prevent bruising.",
                    "icon": "fa-solid fa-hand"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Drink extra fluids to replenish after blood collection.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "Monitor Site",
                    "desc": "Watch for any unusual swelling or discomfort at puncture site.",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "Follow Instructions",
                    "desc": "Adhere to any specific post-test instructions from your nurse.",
                    "icon": "fa-solid fa-list-check"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How accurate are home blood tests compared to lab tests?",
                    "answer": "Our home blood tests are just as accurate as lab tests because we use the same certified laboratories and collection methods. All samples are processed in CAP-accredited labs."
                },
                {
                    "question": "What tests cannot be done at home?",
                    "answer": "Most routine tests can be done at home. Certain specialized tests requiring immediate processing may need a lab visit - our team will advise you during booking."
                },
                {
                    "question": "Is there an extra charge for home service?",
                    "answer": "We offer competitive pricing with minimal service fee for the home visit. Many find the convenience well worth the small additional cost."
                },
                {
                    "question": "How soon can I get an appointment?",
                    "answer": "We typically offer same-day or next-day appointments based on availability in your area."
                },
                {
                    "question": "Do you service all areas of Dubai?",
                    "answer": "Yes, our DHA-certified nurses cover all areas of Dubai including Dubai Marina, Jumeirah, Downtown, and surrounding communities."
                },
                {
                    "question": "Can I get tested on weekends or evenings?",
                    "answer": "Yes, we offer flexible scheduling including weekends and evenings to accommodate your needs."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/blood-test-dubai-large.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 355,
            "title": "Basic Wellness Blood Test Package",
            "price": 0,
            "description": "Comprehensive basic health screening package performed in the comfort of your home. Includes complete blood count, cholesterol test, blood sugar test and more. Conducted by DHA-certified nurses with results reviewed by licensed pathologists."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "فحص الدم في المنزل في دبي | عيادات ايليت بودي هوم",
            "description": "خدمات فحص الدم المنزلية بدبي مع ممرضات معتمدات من هيئة الصحة. أكثر من 2000 فحص متاح بما في ذلك اختبارات الحساسية، فحص السكري، اختبارات الأمراض المنقولة جنسيًا وفحوصات الجسم الكاملة. احجز موعدك اليوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/blood-test-at-home-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "فحص الدم في المنزل في دبي | عيادات ايليت بودي هوم",
                "description": "خدمات فحص الدم المنزلية بدبي مع ممرضات معتمدات من هيئة الصحة. أكثر من 2000 فحص متاح بما في ذلك اختبارات الحساسية، فحص السكري، اختبارات الأمراض المنقولة جنسيًا وفحوصات الجسم الكاملة. احجز موعدك اليوم.",
                "url": "https://elitebodyhome.com/ar/services/blood-test-at-home-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Blood-Test.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Blood-Test.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "فحص الدم المنزلي دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "فحص الدم في المنزل في دبي | عيادات ايليت بودي هوم",
                "description": "خدمات فحص الدم المنزلية بدبي مع ممرضات معتمدات من هيئة الصحة. أكثر من 2000 فحص متاح بما في ذلك اختبارات الحساسية، فحص السكري، اختبارات الأمراض المنقولة جنسيًا وفحوصات الجسم الكاملة. احجز موعدك اليوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Blood-Test.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "فحص الدم في المنزل دبي"],
        "hero": {
            "title": "خدمات فحص الدم في المنزل",
            "subtitle": "",
            "description": "لماذا تتنقل لإجراء فحص دم بينما يمكننا أن نأتي إليك؟ تقدم عيادات ايليت بودي هوم خدمات فحص الدم في المنزل، لتجعل فحوصاتك الصحية سهلة، وآمنة، وخالية من التوتر. تجنب الانتظار في العيادات وتمتع براحة الحصول على نتائج دقيقة وموثوقة من منزلك. سواء لفحوصات الصحة الدورية أو التحاليل المتخصصة، يضمن فريقنا الخبير تجربة سلسة وودودة."
        },
        "howItWorks": {
            "title": "خطوات فحص الدم المنزلي",
            "description": "صممت خدمة فحص الدم المنزلي في دبي لتوفير أقصى درجات الراحة مع الحفاظ على أعلى معايير الدقة والنظافة."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "حجز الموعد",
                    "desc": "اتصل بنا أو احجز عبر الإنترنت وسيقوم خبراؤنا بمساعدتك في تحديد الموعد.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "زيارة الممرضة",
                    "desc": "تصل ممرضة معتمدة من هيئة الصحة بدبي إلى موقعك مع جميع المعدات اللازمة.",
                    "icon": "fa-solid fa-user-nurse"
                },
                {
                    "title": "سحب العينة",
                    "desc": "سحب عينة دم آمن وسريع يتم إكماله في 10-15 دقيقة.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "معالجة العينة",
                    "desc": "إرسال العينات إلى مختبراتنا المعتمدة من الكلية الأمريكية لعلماء الأمراض للتحليل.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "استلام النتائج",
                    "desc": "احصل على نتائجك إلكترونيًا في نفس اليوم عبر البريد الإلكتروني أو بوابة آمنة.",
                    "icon": "fa-solid fa-envelope"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد فحص الدم المنزلي",
            "subheading": "لماذا يعد فحص الدم المنزلي خيارًا مثاليًا",
            "para": "يمنحك اختيار فحص الدم المنزلي العديد من المزايا التي تجعل هذه الخدمة الخيار الأمثل لكثير من المرضى:",
            "items": [
                {
                    "title": "الراحة",
                    "desc": "لا حاجة للانتقال أو الانتظار في الطوابير الطويلة - يمكنك إجراء الفحص في الوقت والمكان الذي يناسبك.",
                    "icon": "fa-solid fa-thumbs-up"
                },
                {
                    "title": "السهولة",
                    "desc": "خدمة مثالية لكبار السن، أو الأطفال، أو أصحاب الهمم الذين يواجهون صعوبة في التنقل.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "توفير الوقت",
                    "desc": "تجنب الانتظار لساعات في المختبرات أو المستشفيات - الإجراء بأكمله سريع وفعال.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "الخصوصية",
                    "desc": "استمتع بتجربة فحص خاصة وسرية بعيدًا عن الأماكن العامة.",
                    "icon": "fa-solid fa-user-secret"
                }
            ]
        },
        "eligibility": {
            "heading": "الفئات المستفيدة",
            "subheading": "",
            "para": "تناسب خدمة فحص الدم المنزلي الفئات التالية:",
            "items": [
                {
                    "title": "المهنيون المشغولون",
                    "desc": "الأشخاص الذين لديهم جداول زمنية مزدحمة ولا يمكنهم زيارة المختبرات خلال ساعات العمل.",
                    "icon": "fa-solid fa-briefcase"
                },
                {
                    "title": "كبار السن",
                    "desc": "المسنون الذين يجدون صعوبة في التنقل إلى المرافق الطبية.",
                    "icon": "fa-solid fa-wheelchair"
                },
                {
                    "title": "أولياء الأمور",
                    "desc": "العائلات التي تريد تجنب ضغوط اصطحاب الأطفال إلى المختبرات.",
                    "icon": "fa-solid fa-child"
                },
                {
                    "title": "أصحاب المناعة الضعيفة",
                    "desc": "الذين يحتاجون إلى الحد من التعرض للأماكن العامة لأسباب صحية.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "preparationTips": {
            "heading": "كيفية الاستعداد",
            "subheading": "",
            "para": "لضمان دقة نتائج الفحص، يرجى اتباع إرشادات التحضير التالية:",
            "items": [
                {
                    "title": "متطلبات الصيام",
                    "desc": "بعض الفحوصات تتطلب الصيام - تأكد من فريقنا عند الحجز.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "الحفاظ على رطوبة الجسم",
                    "desc": "اشرب الكثير من الماء قبل الفحص لتسهيل سحب الدم.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "معلومات الأدوية",
                    "desc": "احتفظ بمعلومات عن أي أدوية تتناولها حاليًا.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "ملابس مريحة",
                    "desc": "ارتدِ ملابس فضفاضة تسمح بالوصول السهل إلى ذراعك.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "فهم نتائجك",
            "subheading": "",
            "para": "يضمن تقريرنا الشامل فهمًا سهلاً لحالتك الصحية:",
            "items": [
                {
                    "title": "نتائج سريعة",
                    "desc": "معظم النتائج متاحة في نفس اليوم عبر التسليم الرقمي الآمن.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "مراجعة الخبراء",
                    "desc": "جميع النتائج تتم مراجعتها من قبل أخصائيي علم الأمراض المعتمدين من هيئة الصحة بدبي لضمان الدقة.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "تنسيق واضح",
                    "desc": "تقارير سهلة القراءة مع تحديد النطاقات المرجعية.",
                    "icon": "fa-solid fa-file-lines"
                },
                {
                    "title": "دعم المتابعة",
                    "desc": "توجيه متاح إذا كانت لديك أسئلة حول نتائجك.",
                    "icon": "fa-solid fa-headset"
                }
            ]
        },
        "aftercareTips": {
            "heading": "بعد الفحص",
            "subheading": "",
            "para": "بعد سحب الدم، نوصي بهذه الخطوات البسيطة:",
            "items": [
                {
                    "title": "الضغط على مكان السحب",
                    "desc": "حافظ على الضغط على موقع البزل لمنع الكدمات.",
                    "icon": "fa-solid fa-hand"
                },
                {
                    "title": "الحفاظ على رطوبة الجسم",
                    "desc": "اشرب سوائل إضافية لتعويض ما بعد جمع الدم.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "مراقبة الموقع",
                    "desc": "راقب أي تورم أو انزعاج غير عادي في موقع البزل.",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "اتباع التعليمات",
                    "desc": "التزم بأي تعليمات محددة بعد الاختبار من ممرضتك.",
                    "icon": "fa-solid fa-list-check"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "ما مدى دقة فحوصات الدم المنزلية مقارنة بفحوصات المختبر؟",
                    "answer": "فحوصات الدم المنزلية لدينا دقيقة مثل فحوصات المختبر لأننا نستخدم نفس المختبرات المعتمدة وطرق الجمع. تتم معالجة جميع العينات في مختبرات معتمدة من الكلية الأمريكية لعلماء الأمراض."
                },
                {
                    "question": "ما هي الفحوصات التي لا يمكن إجراؤها في المنزل؟",
                    "answer": "يمكن إجراء معظم الفحوصات الروتينية في المنزل. قد تتطلب بعض الفحوصات المتخصصة التي تحتاج إلى معالجة فورية زيارة المختبر - سينصحك فريقنا أثناء الحجز."
                },
                {
                    "question": "هل هناك رسوم إضافية للخدمة المنزلية؟",
                    "answer": "نحن نقدم أسعارًا تنافسية مع رسوم خدمة بسيطة للزيارة المنزلية. يجد الكثيرون أن الراحة تستحق التكلفة الإضافية الصغيرة."
                },
                {
                    "question": "ما مدى سرعة حصولي على موعد؟",
                    "answer": "نحن نقدم عادة مواعيد في نفس اليوم أو اليوم التالي بناءً على التوفر في منطقتك."
                },
                {
                    "question": "هل تخدمون جميع مناطق دبي؟",
                    "answer": "نعم، تغطي ممرضاتنا المعتمدة من هيئة الصحة بدبي جميع مناطق دبي بما في ذلك دبي مارينا، جميرا، داون تاون، والمجتمعات المحيطة."
                },
                {
                    "question": "هل يمكنني إجراء الفحص في عطلة نهاية الأسبوع أو المساء؟",
                    "answer": "نعم، نقدم جدولاً مرنًا يتضمن عطلات نهاية الأسبوع والمساء لتلبية احتياجاتك."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}